import React, { FC, useState, useEffect } from 'react';
import { ReactComponent as AssignRiderIcon } from 'assets/icons/assign-rider-modal-icon.svg';
import { toast } from 'react-toastify';
import './styles.scss';
import {
  BaseModal,
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Input,
  type MultiselectItem
} from '@cokitchen/cokitchen-components';
import { OrderType } from 'models/order.model';
import { apiService } from 'utils/api';
import { RiderType } from 'models/rider.model';
import { ArrowDownIcon } from 'cokitchen-icons';
import { getUserName } from 'utils/functions';

interface AssignRiderModalProps {
  show: boolean;
  onClose: () => void;
  order: OrderType;
}

interface AssignRiderFooterProps {
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const AssignRiderFooter: FC<AssignRiderFooterProps> = ({ onCancel, onConfirm, loading }) => (
  <div className='grid grid-cols-2 gap-x-4'>
    <Button small color='primary' variant='outline' className='mx-auto' onClick={onCancel}>
      Cancel
    </Button>
    <Button
      small
      color='primary'
      variant='block'
      className='mx-auto'
      loading={loading}
      onClick={onConfirm}>
      Confirm
    </Button>
  </div>
);

const AssignRiderModal: FC<AssignRiderModalProps> = ({ show, onClose, order }) => {
  const [loading, setLoading] = useState(false);
  const [riders, setRiders] = useState<MultiselectItem[]>([]);
  const [filteredRiders, setFilteredRiders] = useState<MultiselectItem[]>([]);
  const [selectedRider, setSelectedRider] = useState({} as RiderType);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchEmpty, setSearchEmpty] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const assignRider = async () => {
    try {
      setLoading(true);
      await apiService.post('logistics/order/rider/change', {
        data: {
          order_id: order.id,
          rider_id: selectedRider.id
        },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      onClose();
    } catch (err: any) {
      toast(err.response.data.message);
    } finally {
      setLoading(false);
    }
  };
  const loadRiders = async () => {
    const res = await apiService.get('logistics/riders/active', {
      baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
    });
    setRiders(res.data.riders ?? []);
    setFilteredRiders(res.data.riders ?? []);
  };
  useEffect(() => {
    if (show) {
      loadRiders();
      setSelectedRider({} as RiderType);
    }
  }, [show]);

  useEffect(() => {
    setShowDropdown(false);
  }, [selectedRider]);

  const handleSearch = () => {
    const value = searchValue;
    const filtered = riders.filter((rider) =>
      getUserName(rider).toLowerCase().includes(value.toLowerCase())
    );
    setFilteredRiders(filtered);
    setSearchEmpty(filtered.length === 0);
  };

  return (
    <BaseModal
      isOpen={show}
      title='Assign Rider'
      description={`Assign a rider to this order #${order?.order_code?.toUpperCase() ?? ''}`}
      titleIcon={<AssignRiderIcon />}
      titleIconColor='info'
      onRequestClose={onClose}
      footer={<AssignRiderFooter loading={loading} onCancel={onClose} onConfirm={assignRider} />}>
      <Dropdown
        visible={showDropdown}
        onClickOutside={() => setShowDropdown(false)}
        content={
          <div
            onClick={() => setShowDropdown(!showDropdown)}
            className='px-3 py-4 flex items-center justify-between text-sm font-medium rounded-md bg-white border border-gray-6'>
            <span>
              {selectedRider.id ? (
                getUserName(selectedRider)
              ) : (
                <span className='text-gray-5'>Select rider</span>
              )}
            </span>
            <ArrowDownIcon className='w-5 h-5' />
          </div>
        }>
        <DropdownList className='min-w-[300px]'>
          <div className='sticky pl-1 top-0 h-[45px]'>
            <Input
              className='text-sm'
              placeholder='Search rider'
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                handleSearch();
              }}
              inputClass='h-[44px] text-sm'
            />
          </div>

          {loading ? (
            <DropdownItem isLabel>Loading...</DropdownItem>
          ) : searchEmpty ? (
            <DropdownItem className='text-gray-500 mt-2'>
              There are no riders like {searchValue}
            </DropdownItem>
          ) : (
            filteredRiders.map((p, i) => (
              <DropdownItem key={i} onClick={() => setSelectedRider(p)}>
                <Input
                  className='text-sm'
                  type='radio'
                  checked={selectedRider.id === p.id}
                  readOnly
                  label={getUserName(p)}
                />
              </DropdownItem>
            ))
          )}
        </DropdownList>
      </Dropdown>
    </BaseModal>
  );
};

export default AssignRiderModal;
